import React, { useMemo } from "react";

/**
 * Spec strip — UI v2, column bindings corrected against the REAL
 * MafiaTables schema (verified from the legacy Inspect.vb label-fill
 * code and the table DDL — the earlier version guessed "Tool" and
 * "Description", neither of which exists, so several legacy fields
 * never displayed):
 *
 *   legacy label          column              shown as
 *   ─────────────────────────────────────────────────────────────
 *   txtDimType            DimType             title tag (UPPER, legacy)
 *   txtMin/Nominal/Max    Min/Nominal/Max     stat blocks
 *   txtUnitMeasurement    UnitMeasurement     unit tag
 *   GAGE TO USE (lblTool) SpecialGages        gage tag
 *   TOLERANCE CLASS       ToleranceClass      tag
 *   METHOD OF RECORDING   MethodOfRecording   tag
 *   WI / ATP NUMBER       WIATPNum            tag
 *   lblSpecialInstr       SpecialInstr        bold instruction line
 *   lblComment (blue)     AddlInfo            accent note line
 *   lblCheckPieces        (computed)          "Check Pieces" stat
 */
const DnaDetailsHeader = ({ selectedDna, checkPieces }) => {
  const header = useMemo(() => {
    if (!selectedDna) return null;
    const pick = (...keys) => {
      for (const k of keys) {
        const v = selectedDna?.[k];
        if (v !== undefined && v !== null && String(v).trim() !== "")
          return String(v).trim();
      }
      return "";
    };
    return {
      dna: pick("DNANum", "dnaNum"),
      dimType: pick("DimType", "dimType"),
      method: pick("MethodOfRecording", "methodOfRecording"),
      min: pick("Min", "min"),
      nominal: pick("Nominal", "nominal"),
      max: pick("Max", "max"),
      unit: pick("UnitMeasurement", "UnitOfMeasurement", "unitMeasurement"),
      gage: pick("SpecialGages", "Tool", "specialGages"),
      toleranceClass: pick("ToleranceClass", "toleranceClass"),
      wiAtp: pick("WIATPNum", "wiAtpNum"),
      specialInstr: pick("SpecialInstr", "specialInstr"),
      addlInfo: pick("AddlInfo", "addlInfo"),
    };
  }, [selectedDna]);

  if (!selectedDna) {
    return (
      <div className="di-panel">
        <div className="di-panel__body text-muted">
          Select an operation and a DNA to see its specification.
        </div>
      </div>
    );
  }

  // Round 3 (DNA metadata card): readable label-over-value field.
  // Replaces the cramped single-line badges so long values such as
  // "A - Measure Variable, Record in SSW Form" wrap cleanly instead of
  // being squeezed/ellipsized. Label text is the legacy caption verbatim
  // ("Method of Recording", not "Method"); the underlying field key stays
  // MethodOfRecording (no DB/DTO change).
  const field = (label, value) =>
    value !== "" ? (
      <div className="di-meta-field">
        <div className="di-meta-field__label">{label}</div>
        <div className="di-meta-field__value">{value}</div>
      </div>
    ) : null;

  const stat = (label, value, extraClass = "") =>
    value !== "" ? (
      <div className={`di-stat ${extraClass}`}>
        <div className="di-stat__label">{label}</div>
        <div className="di-stat__value">{value}</div>
      </div>
    ) : null;

  return (
    <div className="di-panel">
      <div className="di-spec">
        <div className="di-spec__id" style={{ minWidth: 260, flex: 1 }}>
          <div className="d-flex align-items-baseline gap-2 flex-wrap">
            <div className="di-spec__dna">DNA {header.dna || "—"}</div>
            {header.dimType ? (
              <span className="di-spec__dimtype">
                {header.dimType.toUpperCase()}
              </span>
            ) : null}
          </div>

          {/* Round 3: readable metadata grid (label over value, wraps). */}
          <div className="di-meta-grid">
            {field("Gage to Use", header.gage)}
            {field("Tolerance Class", header.toleranceClass)}
            {field("Method of Recording", header.method)}
            {field("Unit", header.unit)}
            {field("WI / ATP Number", header.wiAtp)}
          </div>

          {header.specialInstr ? (
            <div className="di-spec__instr">{header.specialInstr}</div>
          ) : null}
          {header.addlInfo ? (
            <div className="di-spec__note">{header.addlInfo}</div>
          ) : null}
        </div>

        <div className="di-spec__stats">
          {stat("Min", header.min)}
          {stat("Nominal", header.nominal, "di-stat--nominal")}
          {stat("Max", header.max)}
          {Number(checkPieces) > 0
            ? stat("Check Pieces", String(checkPieces), "di-stat--check")
            : null}
        </div>
      </div>

      {/* Scoped styles for the Round 3 metadata grid. Self-contained so
          di-theme.css is not modified. Reuses the same neutral palette as
          the rest of the spec strip. */}
      <style>{`
        .di-meta-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 6px 10px;
          margin-top: 8px;
        }
        .di-meta-field {
          min-width: 0;
          padding: 4px 8px;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          background: #fafbfc;
        }
        .di-meta-field__label {
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #6c757d;
          margin-bottom: 1px;
        }
        .di-meta-field__value {
          font-size: 0.84rem;
          font-weight: 600;
          color: #212529;
          line-height: 1.3;
          overflow-wrap: anywhere;
          white-space: normal;
        }
      `}</style>
    </div>
  );
};

export default React.memo(DnaDetailsHeader);
